import { memo } from 'react';
import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import {
    IMenuByTypesChild,
    IMenuByTypesItem,
    MenuType,
} from '@r1-frontend/api-domru/api-content/v2/menu/by-types/dto/IMenuByTypesItem';

import DescriptionButton from './childItems/DescriptionButton';
import DescriptionButtonImage from './childItems/DescriptionButtonImage';
import DescriptionLabel from './childItems/DescriptionLabel';

import * as ST from './styled';

interface IProps {
    mainMenuItem: IMenuByTypesItem,
    itemRef?: (instance: HTMLLIElement | null) => void,
    onDataLayerHandler: (menuType: 'Main' | 'Right', title: string) => void,
}

const getItemByType = (
    currentPath: string,
    item: IMenuByTypesChild,
    dataLayerHandler: (menuType: 'Main' | 'Right', title: string,) => void,
) => {
    const isOnlyPromoChildren = item.type === MenuType.DESCRIPTION_BUTTON_IMAGE && item.children.length > 1;

    switch (item.type) {
        case MenuType.DESCRIPTION_BUTTON_IMAGE:
            return item.children.map(child => (
                <DescriptionButtonImage
                    key={`desktop-button-img-${child.id || child.link}`}
                    item={child}
                    isOnlyPromo={isOnlyPromoChildren}
                    onClickHandler={dataLayerHandler}
                />
            ));
        case MenuType.DESCRIPTION_BUTTON:
            return (
                <ST.TypeWrapper key={item.title}>
                    <ST.TypeTitle>{item.title}</ST.TypeTitle>
                    <ST.TypeContent type={item.type}>
                        {item.children.map(child => (
                            <DescriptionButton
                                key={`desktop-button-$${child.id || child.link}`}
                                item={child}
                                onClickHandler={dataLayerHandler}
                            />
                        ))}
                    </ST.TypeContent>
                </ST.TypeWrapper>
            );
        case MenuType.DESCRIPTION_LABEL:
            return (
                <ST.TypeWrapper key={item.title}>
                    <ST.TypeTitle>{item.title}</ST.TypeTitle>
                    <ST.TypeContent type={item.type}>
                        {item.children.map(child => (
                            <DescriptionLabel
                                key={`description-label-${child.id || child.link}`}
                                item={child}
                                onClickHandler={dataLayerHandler}
                            />
                        ))}
                    </ST.TypeContent>
                </ST.TypeWrapper>
            );
        case MenuType.COMMON:
        default:
            return (
                <ST.TypeWrapper key={item.title}>
                    <ST.TypeTitle>{item.title}</ST.TypeTitle>
                    <ST.TypeContent type={item.type}>
                        {item.children.map(child => (
                            <ST.CommonTitle
                                key={`desktop-common-${child.id || child.link}`}
                                isCurrent={child.link === currentPath}
                                title={child.title}
                                href={child.link}
                                onClickHandler={dataLayerHandler}
                            />
                        ))}
                    </ST.TypeContent>
                </ST.TypeWrapper>
            );
    }
};

const MainMenuItem = ({
    mainMenuItem,
    itemRef,
    onDataLayerHandler,
}: IProps): JSX.Element => {
    const { asPath } = useRouter();

    return (
        <ST.MenuItemWrapper
            ref={itemRef}
            isPromo={!!mainMenuItem.promoted}
        >
            {mainMenuItem.children?.length
                ? <>
                    <ST.TextItem>{mainMenuItem.title}</ST.TextItem>
                    <ST.CollapsedMenu>
                        <ST.CollapsedContainer>
                            {mainMenuItem.children.map((child, index) => {
                                return <React.Fragment key={`${child.type}-${index}`}>
                                   {getItemByType(asPath, child, onDataLayerHandler)}
                                </React.Fragment>;
                            })}
                        </ST.CollapsedContainer>
                    </ST.CollapsedMenu>
                </>
                : <Link
                    href={mainMenuItem.link}
                    prefetch={false}
                    passHref
                >
                    <ST.LinkItem
                        isCurrent={asPath === mainMenuItem.link}
                        onClick={() => onDataLayerHandler('Main', mainMenuItem.title)}
                        data-test='link-item'
                    >
                        {mainMenuItem.title}
                    </ST.LinkItem>
                </Link>
            }
        </ST.MenuItemWrapper>
    );
};

export default memo(MainMenuItem);
